// Client-side logging utility for production error tracking

interface LogEntry {
  level: 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
  url?: string;
  userAgent?: string;
  stack?: string;
  userId?: string;
  roomId?: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private apiEndpoint = '/api/log';

  private formatMessage(level: string, message: string, extra?: Record<string, unknown>): LogEntry {
    return {
      level: level as LogEntry['level'],
      message,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      stack: typeof extra?.stack === 'string' ? extra.stack : undefined,
      userId: typeof extra?.userId === 'string' ? extra.userId : undefined,
      roomId: typeof extra?.roomId === 'string' ? extra.roomId : undefined,
    };
  }

  private async sendToServer(logEntry: LogEntry) {
    if (this.isDevelopment) {
      return; // Don't send logs to server in development
    }

    try {
      await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry),
      });
    } catch (error) {
      // Silently fail if logging endpoint is not available
      console.warn('Failed to send log to server:', error);
    }
  }

  info(message: string, extra?: Record<string, unknown>) {
    const logEntry = this.formatMessage('info', message, extra);
    
    if (this.isDevelopment) {
      console.log(`[INFO] ${message}`, extra);
    }
    
    this.sendToServer(logEntry);
  }

  warn(message: string, extra?: Record<string, unknown>) {
    const logEntry = this.formatMessage('warn', message, extra);
    
    console.warn(`[WARN] ${message}`, extra);
    this.sendToServer(logEntry);
  }

  error(message: string, error?: Error, extra?: Record<string, unknown>) {
    let stack: string | undefined;
    
    if (error instanceof Error) {
      stack = error.stack;
    }

    const logEntry = this.formatMessage('error', message, {
      ...extra,
      stack,
    });
    
    console.error(`[ERROR] ${message}`, error, extra);
    this.sendToServer(logEntry);
  }

  // Game-specific logging methods
  gameError(message: string, error?: Error, gameContext?: { roomId?: string; playerId?: string }) {
    this.error(message, error, gameContext);
  }

  socketError(message: string, error?: Error, socketContext?: { event?: string; data?: unknown; socketUrl?: string }) {
    this.error(`Socket Error: ${message}`, error, socketContext);
  }
}

// Global error handler
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('Unhandled JavaScript error', event.error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', new Error(event.reason), {
      reason: event.reason,
    });
  });
}

export const logger = new Logger();
export default logger;